import { useMemo } from "react";
import { z } from "zod";
import {
  TreemapChart,
  WidgetEmpty,
  WidgetInteractive,
  WidgetSkeleton,
  useCategoricalColors,
  useWidgetInstance,
  type TreemapChartTile,
  type WidgetModule,
  type WidgetRenderContext,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import { groupBySchema, useGroupDimension } from "#app/widgets/dashboards/use-dimension-counts";
import {
  MISSING_FIELD_DESCRIPTION,
  MISSING_FIELD_TITLE,
  groupBySettingField,
  useDimensionSlices,
} from "#app/widgets/dashboards/use-dimension-slices";
import {
  MEASURE_SETTING_FIELD,
  calculateSchema,
  measureFieldId,
  measureSchema,
  measureSettingFields,
  useFieldMeasures,
  useMeasureFormat,
  withLegacyDataKeys,
} from "#app/widgets/dashboards/widget-measure";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { portfolioTitle } from "../shared/derived-titles";

/**
 * `portfolio-map` — a treemap of projects tiled by any grouping dimension, on
 * the `TreemapChart` finished chart. The settings schema drives it end to end:
 * the `dimension` select, an inner-padding `number`, a top-N cap, and a value
 * toggle. Tiles size by the count per group, or by a field aggregate under a
 * field Measure (`calculate`, `widget-measure.ts`). Every tile wears its entity's
 * OWN color (`useCategoricalColors` over the capped leaves — the chart keys
 * each tile's color to its datum, so layout reordering never repaints a
 * category).
 */
export const portfolioMapConfigSchema = withLegacyDataKeys(
  z.looseObject({
    dimension: groupBySchema.default("type"),
    measure: measureSchema,
    calculate: calculateSchema,
    padding: z.int().min(0).max(8).default(3),
    maxTiles: z.int().min(2).max(16).default(10),
    showValues: z.boolean().default(true),
    filters: widgetFiltersSchema.nullish(),
  }),
  { groupBy: "dimension" },
);

export type PortfolioMapConfig = z.infer<typeof portfolioMapConfigSchema>;

function PortfolioMapRender({ config }: WidgetRenderContext<PortfolioMapConfig>) {
  const { asked, pending: measurePending } = useFieldMeasures(config.measure, config.calculate);
  const grouped = useGroupDimension(config.dimension);
  const pending = grouped === null || measurePending;
  const dimension = pending ? null : grouped;
  const { data, loading, error } = useDimensionSlices(dimension, config.filters, "creationDate", asked);
  const measure = useMeasureFormat(asked);
  // Click-through: a tile press opens this card's Data drawer on the category's rows.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();
  const emptyTitle = useWindowEmptyTitle("No Projects", config.filters);

  const leaves = useMemo(() => data.slice(0, Math.max(2, config.maxTiles)), [data, config.maxTiles]);
  const sliceColors = useCategoricalColors(leaves);
  const tiles = useMemo(
    () => leaves.map((d) => ({ id: d.id, label: d.label, value: d.total })),
    [leaves],
  );

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="treemap" />;
  }
  if (leaves.length === 0) {
    return pending ? (
      <WidgetEmpty title={MISSING_FIELD_TITLE} description={MISSING_FIELD_DESCRIPTION} />
    ) : (
      <WidgetEmpty title={emptyTitle} />
    );
  }

  const handleTileClick =
    drill && instance && dimension
      ? (tile: TreemapChartTile) => {
          if (!tile.id) return;
          drill.openData(instance.instanceId, {
            kind: "dimension",
            dimension,
            ids: [tile.id],
            label: tile.label,
          });
        }
      : undefined;

  return (
    <WidgetInteractive fill="flex">
      <TreemapChart
        data={tiles}
        colorTokens={sliceColors.tokens}
        showValues={config.showValues}
        formatValue={measure.compact}
        padding={config.padding}
        onTileClick={handleTileClick}
        ariaLabel={measure.measure ? `${measure.label} portfolio map` : "Project portfolio map"}
        renderTooltip={(tile) => {
          const count = leaves.find((leaf) => leaf.id === tile.id)?.count ?? tile.value;
          return <WidgetChartTooltip label={tile.label} details={measure.detail(tile.value, count)} />;
        }}
      />
    </WidgetInteractive>
  );
}

export const portfolioMapDef: WidgetModule<PortfolioMapConfig> = {
  type: "portfolio-map",
  title: "Portfolio map",
  dynamicTitle: (config, context) => portfolioTitle(config, config.dimension, context),
  icon: "layout-grid",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 5 },
  configSchema: portfolioMapConfigSchema,
  headerActions: WidgetHeaderFilters,
  settings: (config) => [
    {
      title: "Data",
      fields: [
        // The treemap's dimension key predates the shared `groupBy` name; the
        // row is the shared one under this module's own key.
        groupBySettingField("dimension"),
        ...measureSettingFields(MEASURE_SETTING_FIELD, measureFieldId(config.measure) !== null),
        { key: "maxTiles", kind: "number", label: "Max tiles", min: 2, max: 16, step: 1 },
      ],
    },
    {
      title: "Layout",
      fields: [{ key: "padding", kind: "number", label: "Tile gap", min: 0, max: 8, step: 1 }],
    },
    {
      title: "Labels",
      fields: [{ key: "showValues", kind: "toggle", label: "Show values" }],
    },
    {
      title: "Filters",
      fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
    },
  ],
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "portfolio-map.workflow-map",
      label: "Workflow map",
      description: "A treemap of workflow work by type, stage, team, assignee, or follow up.",
      section: "workflows" satisfies WidgetSection,
      tags: ["projects", "types", "stages", "teams", "members", "ageHealth"] satisfies readonly WidgetTag[],
      defaultConfig: { dimension: "stage", maxTiles: 10, showValues: true },
      locks: ["dimension"],
      defaultW: 5,
    },
    {
      key: "portfolio-map.treemap",
      label: "Treemap",
      description: "Compare grouped counts or field totals as proportional tiles.",
      section: "custom" satisfies WidgetSection,
      // Lands named by its gallery label. The label is not the family's while
      // every choice is built in, so it sticks like a rename until the user
      // renames the card or brings a custom field in — then the title derives
      // from the field ("Deductible by Bid Type").
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: { dimension: "type", maxTiles: 10, showValues: true },
      defaultW: 5,
    },
  ],
  render: PortfolioMapRender,
};
